import { useRuntimeConfig } from '#imports'

import {
  type MarketCountryCode,
  type Price,
  merchantsAPI,
  sellerOpportunities,
} from '@backmarket/http-api'
import { patchListing } from '@backmarket/http-api/src/api-specs-catalog/catalog-access'
import { deletePricingOpportunity } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-opportunities'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'

const { LISTING_PRICE_DIFFERENCE_LIMIT_IN_PERCENTAGE } =
  // TODO https://imperatorz.slack.com/archives/C544PFLFL/p1756305968322459
  // eslint-disable-next-line @backmarket/custom-rules/no-root-scope-composable
  useRuntimeConfig().public

// This function calculate the maximum target price for a listing.
// For instance, if the maximum range is 15% of the target price,
// then the formula to find the target price based on the min price is: min price / 0.85 (and not min price * 1.15)
function getMaximumTargetPrice(minimumPrice: number) {
  const LIMIT_IN_PERCENTAGE =
    Number(LISTING_PRICE_DIFFERENCE_LIMIT_IN_PERCENTAGE) || 85

  return Math.floor(minimumPrice / (LIMIT_IN_PERCENTAGE / 100)).toFixed(2)
}

// CASE 1: The seller does not have the backpricer activated,
// so we update the listing with the given new price
async function updateListingPriceWithoutBackpricerEnabled({
  listingId,
  marketToUpdate,
  newPrice,
}: {
  listingId: string | number
  marketToUpdate: MarketCountryCode
  newPrice: Price
}) {
  await $httpFetch(patchListing, {
    pathParams: { listingId },
    body: {
      markets: {
        [marketToUpdate]: {
          price: {
            amount: newPrice.amount.toString(),
            currency: newPrice.currency,
          },
        },
      },
    },
  })
}

// CASE 2: The seller has the backpricer activated, two possible sub cases:
// - if the seller has only the minimum price defined (such as in the opportunity page),
// we calculate and set the targeted price to the maximum price possible within the range
// - if the seller has both minimum price and targeted price defined (such as in the listing page),
// we check if the wanted targeted price is not above the maximum possible price. If it is, then we decrease it to the maximum possible price.
async function updateListingPriceWithBackpricerEnabled({
  listingId,
  marketToUpdate,
  minimumPrice,
  targetedPrice,
}: {
  listingId: string | number
  marketToUpdate: MarketCountryCode
  minimumPrice: Price
  targetedPrice?: Price
}) {
  const maxAllowedPrice = getMaximumTargetPrice(Number(minimumPrice.amount))

  // if there is no targeted price, then we consider that it is not in range,
  // to set it to the maximum price possible
  const targetedPriceIsNotInRange =
    !targetedPrice?.amount ||
    Number(targetedPrice?.amount) > Number(maxAllowedPrice)

  const updatedMarket = {
    minPrice: {
      amount: minimumPrice.amount.toString(),
      currency: minimumPrice.currency,
    },
    price: {
      amount: targetedPriceIsNotInRange
        ? maxAllowedPrice.toString()
        : targetedPrice.amount.toString(),
      currency: minimumPrice.currency,
    },
  }

  await $httpFetch(patchListing, {
    pathParams: { listingId },
    body: {
      markets: {
        [marketToUpdate]: updatedMarket,
      },
    },
  })
}

// This function is here to ease the process of wining a backbox for a given listing.
// When you win a backbox, you also need to delete the related opportunity for the listing (if it exists)
async function winBackboxForListing({
  backboxPrice,
  isBackpricerEnabled,
  listingId,
  marketToUpdate,
  targetedPrice,
}: {
  backboxPrice: Price
  isBackpricerEnabled: boolean
  listingId: string | number
  marketToUpdate: MarketCountryCode
  targetedPrice?: Price
}) {
  if (!isBackpricerEnabled) {
    await updateListingPriceWithoutBackpricerEnabled({
      newPrice: backboxPrice,
      listingId,
      marketToUpdate,
    })
  } else {
    // When the seller click on the get backbox button and has the backpricer feature activated,
    // the backbox price becomes the minimum price
    await updateListingPriceWithBackpricerEnabled({
      minimumPrice: backboxPrice,
      listingId,
      marketToUpdate,
      targetedPrice,
    })
  }

  // when we try to win the backbox for a specific listing, we should delete the according opportunity
  await $httpFetch(deletePricingOpportunity, {
    pathParams: {
      listingId,
      market: marketToUpdate,
    },
  })
}

export async function useListingPriceUpdater() {
  const { data: backpricerStatus } = await useHttpFetch(
    merchantsAPI.getBackPricerStatus,
    {
      default: () =>
        ({ backpricerEnabled: false }) as merchantsAPI.GetBackPricerStatus,
    },
  )

  // function updateListingPrice(listing: unknown) {
  //   // will be used for the listing object
  // }

  // function winBackboxForListing(listing: unknown) {
  //   // will be used for the listing object
  // }

  async function updateListingWithOpportunityPrice(
    opportunity: sellerOpportunities.PricingOpportunity,
  ) {
    if (backpricerStatus.value?.backpricerEnabled) {
      await updateListingPriceWithBackpricerEnabled({
        listingId: opportunity.listingId,
        marketToUpdate: opportunity.market,
        minimumPrice: opportunity.recommendedPrice,
      })
    } else {
      await updateListingPriceWithoutBackpricerEnabled({
        listingId: opportunity.listingId,
        marketToUpdate: opportunity.market,
        newPrice: opportunity.recommendedPrice,
      })
    }
  }

  async function winBackboxForOpportunity(
    opportunity: sellerOpportunities.PricingOpportunity,
  ) {
    await winBackboxForListing({
      listingId: opportunity.listingId,
      marketToUpdate: opportunity.market,
      backboxPrice: opportunity.priceInformation.backboxPrice,
      targetedPrice: opportunity.priceInformation.price,
      isBackpricerEnabled: backpricerStatus.value?.backpricerEnabled,
    })
  }

  return { updateListingWithOpportunityPrice, winBackboxForOpportunity }
}
